import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { StoreCurrency } from 'app-types/core';
import { useGetDeskInfo } from 'data-layer';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { match } from 'ts-pattern';

import { getFromLocalStoragePlanInfo } from '../../../../organisms';
import { PricingPlanProps } from '../types/pricingTypes';
import { SubscriptionContext } from './useSubscriptionContext';

dayjs.extend(utc);

const usePricingBlocker = (updateSubscriptionInfo: () => void) => {
  const [step, setStep] = useState<PriceStep>(PriceStep.Price);

  const contextData = useContext(SubscriptionContext);

  const { hasActivatedDesk } = useGetDeskInfo(contextData?.subscription);
  const hasActivePlan = contextData?.subscription?.isActive;

  const activePricingPlan: PricingPlanProps = {
    isDeskIncluded: hasActivePlan ? hasActivatedDesk : false,
    plan: contextData?.subscription?.plan,
    nextPlan: contextData?.subscription?.nextPlan,
    nextBillingDate: contextData?.subscription?.nextRebill,
    isActive: contextData?.subscription?.isActive,
    currency: (contextData?.subscription?.currency as StoreCurrency) || StoreCurrency.Eur,
  };

  const [selectedPlan, setSelectedPlan] = useState<PricingPlanProps>(activePricingPlan);

  const isOnTrial = useMemo(
    () =>
      contextData?.subscription?.endPromo
        ? dayjs.utc(contextData.subscription.endPromo).isAfter()
        : false,
    [contextData?.subscription?.endPromo],
  );

  const handleResetChanges = useCallback(() => {
    setSelectedPlan(activePricingPlan);
  }, [activePricingPlan]);

  const handleChangePlan = useCallback(
    (newSelectedPlan: PricingPlanProps) => {
      setSelectedPlan((state) => ({
        ...state,
        ...newSelectedPlan,
      }));

      const actualPlanPrice =
        contextData?.plans.find((plan) => plan.name === activePricingPlan.plan)?.price || 0;
      const targetPlanPrice =
        contextData?.plans.find((plan) => plan.name === newSelectedPlan.plan)?.price || 0;

      match<boolean>(true)
        .with(isOnTrial, () => setStep(PriceStep.Payment))
        .with(actualPlanPrice > targetPlanPrice && !!contextData?.subscription?.isActive, () =>
          setStep(PriceStep.DowngradePlan),
        )
        .otherwise(() => setStep(PriceStep.Payment));
    },
    [contextData?.subscription, activePricingPlan, isOnTrial],
  );

  const handleChangeDesk = useCallback(
    (plan: PricingPlanProps) => {
      setSelectedPlan(plan);

      if (activePricingPlan.isActive) {
        if (contextData?.subscription?.annual) {
          setStep(PriceStep.RequestFeatureForAnnualClient);
        } else {
          if (plan.isDeskIncluded) {
            setStep(PriceStep.PaymentDesk);
          } else {
            setStep(PriceStep.DowngradeDesk);
          }
        }
      }
    },
    [contextData?.subscription, activePricingPlan],
  );

  useEffect(() => {
    if (step === PriceStep.Cancel) {
      setSelectedPlan((state) => ({ ...state, plan: undefined, isDeskIncluded: false }));
      setStep(PriceStep.DowngradePlan);
    }
  }, [step]);

  useEffect(() => {
    const hasPaymentPlanInfo = getFromLocalStoragePlanInfo();

    if (hasPaymentPlanInfo && !!contextData?.planProgress?.cardActivated) {
      setStep(PriceStep.Payment);
    }
  }, [contextData?.planProgress?.cardActivated]);

  const handleUpdateSubInfo = useCallback(async () => {
    updateSubscriptionInfo();
  }, []);

  const handleSetStep = useCallback((newStep: PriceStep, updateSubscription?: boolean) => {
    updateSubscription && updateSubscriptionInfo();
    setStep(newStep);
  }, []);

  return {
    step,
    selectedPlan,
    activePricingPlan,
    handleSetStep,
    handleUpdateSubInfo,
    handleChangePlan,
    handleChangeDesk,
    handleResetChanges,
    isOnTrial,
  };
};

export enum PriceStep {
  None = 'None',
  Price = 'Price',
  Payment = 'Payment',
  PaymentDesk = 'PaymentDesk',
  Vat = 'Vat',
  DowngradePlan = 'DowngradePlan',
  DowngradeDesk = 'DowngradeDesk',
  RequestCall = 'RequestCall',
  RequestDemo = 'RequestDemo',
  Cancel = 'Cancel',
  RequestFeatureForAnnualClient = 'RequestFeatureForAnnualClient',
}

export { usePricingBlocker };
